import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEOData, generateStructuredData, generateCanonicalUrl, generateHreflangLinks } from '@/utils/seoEnhanced';

interface SEOEnhancedProps extends SEOData {
  structuredDataType?: 'Organization' | 'LocalBusiness' | 'Product' | 'Article' | 'RealEstateAgent';
  breadcrumbs?: Array<{ name: string; url: string }>;
  faqs?: Array<{ question: string; answer: string }>;
  noindex?: boolean;
  children?: React.ReactNode;
  lang?: string;
  alternateLanguages?: boolean;
  canonical?: string;
}

export const SEOEnhanced: React.FC<SEOEnhancedProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  locale = 'en_US',
  author,
  publishedTime,
  modifiedTime,
  structuredDataType,
  breadcrumbs,
  faqs,
  noindex = false,
  children,
  lang = 'en',
  alternateLanguages = true,
  canonical,
  ...rest
}) => {
  const baseUrl = 'https://maskan-student-nest.lovable.app';
  const fullUrl = url || baseUrl;
  const ogImage = image || `${baseUrl}/og-image.jpg`;
  const canonicalUrl = canonical || generateCanonicalUrl(fullUrl.replace(baseUrl, ''), lang);
  
  // Generate structured data
  const structuredData = structuredDataType 
    ? generateStructuredData({ 
        title, 
        description, 
        keywords, 
        image: ogImage, 
        url: fullUrl, 
        structuredDataType,
        ...rest 
      })
    : null;

  // Generate hreflang links
  const hreflangLinks = alternateLanguages 
    ? generateHreflangLinks(fullUrl.replace(baseUrl, ''))
    : [];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Language and Locale */}
      <html lang={lang} />
      <meta property="og:locale" content={locale} />
      
      {/* Robots */}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'} />
      <meta name="googlebot" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Maskan" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:url" content={fullUrl} />
      
      {/* Hreflang Links */}
      {hreflangLinks.map(link => (
        <link
          key={link.hreflang}
          rel="alternate"
          hrefLang={link.hreflang}
          href={link.href}
        />
      ))}
      
      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#6366f1" />
      <meta name="msapplication-TileColor" content="#6366f1" />
      <meta name="application-name" content="Maskan" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Breadcrumb Structured Data */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((crumb, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": crumb.name,
              "item": crumb.url
            }))
          })}
        </script>
      )}
      
      {/* FAQ Structured Data */}
      {faqs && faqs.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      )}
      
      {children}
    </Helmet>
  );
};